import { useCallback, useState } from 'react';

import {
  BAR_SERVICES_TAXONOMY_KEY,
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomyKeyFromId } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { getBusinessCategory } from '@zola-helpers/client/dist/es/tracking/trackingHelper';
import { isDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import Chip from '@zola/zola-ui/src/components/Chip';
import { BuildingStoreIcon } from '@zola/zola-ui/src/components/SvgIconsV3/BuildingStore';
import H from '@zola/zola-ui/src/typography/Headings';

import { Field } from 'react-final-form';

import { loadMiniQuizAnswers } from '~/actions/couples/miniQuizActions';
import { receivedTaxonomyKey } from '~/actions/couples/types/miniQuizActionTypes';
import { fetchUserSettings } from '~/actions/couples/userSettingsActions';
import { useFlowTrackingV2 } from '~/contexts/TrackingContracts/FlowTrackingV2/FlowTrackingV2Context';
import { useUserContext } from '~/contexts/UserContext';
import { getMiniQuizUserSettingForTaxonomy } from '~/pages/couples/explore/components/MiniQuiz/miniQuizUserSettings';
import { useAppDispatch, useAppSelector } from '~/reducers';
import { getQuizzesAnswered } from '~/reducers/couples/quizReducer';
import { getMiniQuizTaxonomyKey } from '~/selectors/couples/miniQuizSelectors';
import { StandardLogFn } from '~/util/logger';
import { ChipOption, getVendorIconV3, vendorCategoryChipOptions } from '~/util/vendorUtils';

import styles from './miniQuizVendorQuestion.module.less';

const vendorCategoryOptions: ChipOption[] = [
  ...vendorCategoryChipOptions,
  {
    icon: getVendorIconV3(BAR_SERVICES_TAXONOMY_KEY),
    hed: 'Bar services',
    value: BAR_SERVICES_TAXONOMY_KEY,
  },
];

const getVendorChips = (
  localTaxonomyKey: SearchableVendorTaxonomyKey | undefined,
  setLocalTaxonomyKey: (val: SearchableVendorTaxonomyKey | undefined) => void,
  vendorCategoriesCompleted: (VendorTaxonomyKey | undefined)[]
) => {
  return vendorCategoryOptions.map(({ icon, hed, value }) => (
    <Field className={styles.chip} key={hed} name="vendorCategory" parse={() => value}>
      {({ input, ...rest }) => {
        return (
          <Chip
            onClick={(event) => {
              input.onChange(event);
              setLocalTaxonomyKey(value);
            }}
            active={localTaxonomyKey === value}
            type="button"
            icon={icon({})}
            hed={hed}
            iconPosition={isDesktop() ? 'left' : 'top'}
            large
            data-testid={`vendor-priority-${value}`}
            disabled={vendorCategoriesCompleted.includes(value)}
            {...{ ...input, ...rest }}
          />
        );
      }}
    </Field>
  ));
};

const MiniQuizVendorQuestion = () => {
  const userContext = useUserContext();
  const dispatch = useAppDispatch();
  const { trackFlowStepCompleted } = useFlowTrackingV2();

  const quizzesAnswered = useAppSelector(getQuizzesAnswered);
  const taxonomyKey = useAppSelector(getMiniQuizTaxonomyKey);
  const [localTaxonomyKey, setLocalTaxonomyKey] = useState(taxonomyKey);

  const isMiniQuiz = ({ quiz }: { quiz: string }) => quiz === 'MTFM_QUIZ';

  const vendorCategoriesCompleted = useCallback(() => {
    const miniQuizzesAnswered = quizzesAnswered.filter(isMiniQuiz);
    return miniQuizzesAnswered.map((quiz) =>
      getVendorTaxonomyKeyFromId(quiz.taxonomyNodeId as number)
    );
  }, [quizzesAnswered]);

  const hed = vendorCategoriesCompleted().length
    ? 'Who do you want to hear from next?'
    : 'Who do you want to hear from first?';

  const loadQuizQuestions = () => {
    if (localTaxonomyKey) {
      dispatch(receivedTaxonomyKey({ taxonomyKey: localTaxonomyKey }));
      const miniQuizAnsweredKey = getMiniQuizUserSettingForTaxonomy(localTaxonomyKey);
      trackFlowStepCompleted({
        // @ts-expect-error The business category prop should be allowed here
        businessCategory: getBusinessCategory(localTaxonomyKey as SearchableVendorTaxonomyKey),
        stepNumber: 1,
      });
      dispatch(loadMiniQuizAnswers(localTaxonomyKey, userContext?.is_guest))
        .then(() => {
          return dispatch(fetchUserSettings([miniQuizAnsweredKey]));
        })
        .catch((error) => {
          StandardLogFn(error);
        });
    }
  };

  return (
    <div className={styles.vendorQuestion}>
      <H.Title2 className={styles.hed} presentation="h6">
        <BuildingStoreIcon width={24} height={24} />
        {hed}
      </H.Title2>
      <div className={styles.content}>
        <div className={styles.chips}>
          {getVendorChips(localTaxonomyKey, setLocalTaxonomyKey, vendorCategoriesCompleted())}
        </div>
        <ButtonV3 disabled={!localTaxonomyKey} size="large" onClick={() => loadQuizQuestions()}>
          Next: your wedding details
        </ButtonV3>
      </div>
    </div>
  );
};

export default MiniQuizVendorQuestion;
